import { GetInvoicesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/invoices/models/inputs/get-invoices-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { InvoiceModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/invoice.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  INVOICE_FILTERS_LSK,
  INVOICE_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  INVOICE_LIST_DEFAULT_OPTIONS,
  INVOICE_LIST_FILTER_NAME_LIST,
  INVOICE_LIST_SORT_FIELD,
  INVOICE_LIST_SORT_ORDER,
  InvoiceListOptionsModel,
} from './invoice-list/invoice-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const InvoiceContext = createContext<InvoiceModel | null>(null);

export const useInvoiceContext = (): InvoiceModel => {
  const context = useContext(InvoiceContext);

  if (!context) {
    throw new Error(
      'useInvoiceContext must be used within InvoiceContext.Provider',
    );
  }

  return context;
};

interface InvoiceContextProviderProps {
  data: InvoiceModel;
  children: React.ReactNode;
}

export const InvoiceContextProvider = ({
  data,
  children,
}: InvoiceContextProviderProps): React.JSX.Element => {
  return (
    <InvoiceContext.Provider value={data}>{children}</InvoiceContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface InvoiceListContextProps {
  getPaginatedQueryParameters: GetInvoicesPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetInvoicesPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
  columnOptions: InvoiceListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<InvoiceListOptionsModel>>;
}

export const InvoiceListContext = createContext<InvoiceListContextProps | null>(
  null,
);

export const useInvoiceListContext = (): InvoiceListContextProps => {
  const context = useContext(InvoiceListContext);

  if (!context) {
    throw new Error(
      'useInvoiceListContext must be used within InvoiceListContext.Provider',
    );
  }

  return context;
};

interface InvoiceListContextProviderProps {
  children: React.ReactNode;
}

export const InvoiceListContextProvider = ({
  children,
}: InvoiceListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetInvoicesPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        customerId: [],
        status: [],
        type: [],
        textToSearch: '',
        // Sortable fields
        sortField: INVOICE_LIST_SORT_FIELD,
        sortOrder: INVOICE_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...INVOICE_LIST_FILTER_NAME_LIST,
      ],
      INVOICE_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<InvoiceListOptionsModel>(
      INVOICE_LIST_DEFAULT_OPTIONS,
      INVOICE_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
      columnOptions,
      setColumnOptions,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  ]);

  return (
    <InvoiceListContext.Provider value={data}>
      {children}
    </InvoiceListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterInvoiceListContext =
  createContext<DeepPartialSkipArrayKey<GetInvoicesPaginatedModel> | null>(
    null,
  );

export const useFilterInvoiceListContext =
  (): DeepPartialSkipArrayKey<GetInvoicesPaginatedModel> => {
    const context = useContext(FilterInvoiceListContext);

    if (!context) {
      throw new Error(
        'useFilterInvoiceListContext must be used within FilterInvoiceListContext.Provider',
      );
    }

    return context;
  };

interface FilterInvoiceListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetInvoicesPaginatedModel>;
  children: React.ReactNode;
}

export const FilterInvoiceListContextProvider = ({
  data,
  children,
}: FilterInvoiceListContextProviderProps): React.JSX.Element => {
  return (
    <FilterInvoiceListContext.Provider value={data}>
      {children}
    </FilterInvoiceListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchInvoiceListContext =
  createContext<DeepPartialSkipArrayKey<GetInvoicesPaginatedModel> | null>(
    null,
  );

export const useFilterSearchInvoiceListContext =
  (): DeepPartialSkipArrayKey<GetInvoicesPaginatedModel> => {
    const context = useContext(FilterSearchInvoiceListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchInvoiceListContext must be used within FilterSearchInvoiceListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchInvoiceListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetInvoicesPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchInvoiceListContextProvider = ({
  data,
  children,
}: FilterSearchInvoiceListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchInvoiceListContext.Provider value={data}>
      {children}
    </FilterSearchInvoiceListContext.Provider>
  );
};
